import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { mapStatusesToCardDetails } from "../../../../helpers/mapStatusesToCardDetails";
import { WSEmptyStateProps } from "@wingspanhq/fe-component-library/dist/lib/components/WSEmptyState/WSEmptyState";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users/dist/lib/interfaces";
import { IdentityVerificationRequestStatus } from "@wingspanhq/users/dist/lib/interfaces/identityVerificationRequest";
import React, { useEffect } from "react";
import { filesService } from "../../../../../../services/files";
import {
  toWSDateString,
  WSImage,
  WSInfoBox,
  WSList
} from "@wingspanhq/fe-component-library";
import emptyDocument from "../../../../../../../src/assets/images/documents/documents.svg";

export const Image: React.FC<{
  fileId: string;
  mimeType: string;
  isLastOne: boolean;
}> = ({ fileId, mimeType, isLastOne }) => {
  const [image, setImage] = React.useState<string | null>(null);
  const [fileUploadedAt, setFileUploadedAt] = React.useState<Date | null>(null);

  useEffect(() => {
    (async () => {
      const file = await filesService.member.private.get(fileId);
      const arrayBuffer = await filesService.member.private.download(
        file.fileId
      );

      const blob = new Blob([arrayBuffer], {
        type: file.mimetype || mimeType || "image/jpeg"
      });
      const src = URL.createObjectURL(blob);

      setImage(src);
      isLastOne && setFileUploadedAt(file.createdAt);
    })();
  }, [fileId, mimeType, isLastOne]);

  if (image) {
    return (
      <>
        <WSImage
          placeholder={<WSImage placeholder={null} src={emptyDocument} />}
          src={image}
          colorBackground="gray200"
        />

        {isLastOne && fileUploadedAt ? (
          <WSInfoBox>Uploaded on {toWSDateString(fileUploadedAt)}</WSInfoBox>
        ) : null}
      </>
    );
  }

  return null;
};

export const payeeIdentityVerificationRenderCardDescription = (
  requirement: IRequirementResponse,
  identityVerificationRequest?: IIdentityVerificationRequestResponse
) => {
  const images = identityVerificationRequest?.files?.filter(
    file => file.imageType
  );

  if (images?.length) {
    return (
      <WSList gap="L">
        {images.map((image, index, all) => (
          <Image
            key={image.fileId}
            mimeType={image.mimeType}
            fileId={image.fileId!}
            isLastOne={index === all.length - 1}
          />
        ))}
      </WSList>
    );
  }

  const descriptions: Record<
    IdentityVerificationRequestStatus,
    WSEmptyStateProps
  > = {
    [IdentityVerificationRequestStatus.Pending]: {
      type: "people",
      title: "ID verification is pending",
      description:
        "Waiting for the contractor to start the ID verification process."
    },
    [IdentityVerificationRequestStatus.Complete]: {
      type: "done",
      title: "ID verification is complete",
      description:
        "ID verification is complete. The results will be shown here soon."
    },
    [IdentityVerificationRequestStatus.Failed]: {
      type: "people",
      title: "Something went wrong with ID verification",
      description: "There was an issue with ID verification."
    }
  };

  return mapStatusesToCardDetails({
    requirement,
    documentStatus:
      identityVerificationRequest?.status ||
      IdentityVerificationRequestStatus.Pending,
    documentStatuses: descriptions,
    pendingApprovalStatus: {
      type: "people",
      title: "ID verification was submitted for review",
      description: "Contractor submitted ID verification for your approval."
    },
    approvedStatus: {
      type: "people",
      title: "ID verification was approved",
      description:
        "ID verification and biometrics matching was approved. No further action is required."
    },
    rejectedStatus: {
      type: "people",
      title: "ID verification was rejected",
      description:
        "ID verification was rejected by you. No further action is required."
    }
  });
};
